/**
 * Default asset manifest (UPPERCASE-hyphen filenames).
 *
 * URLs are relative to `assetsBaseUrl` (default `/models`). The viewer can
 * override entries via the `assetManifest` prop (e.g. for tests, alternative
 * skins, or to plug in user GLBs).
 *
 * Hygienic ATH modules carry per-RailSystem URLs. Standard AT/Eco modules
 * only set `Beckhoff`; the loader silently falls back to that variant when
 * `HepcoGfx` is requested but unavailable.
 *
 * `null` for `Beckhoff` marks "prepared but not delivered"
 * (AT2042 / AT2140) — the loader renders a placeholder AABB mesh.
 */

import type {
  AssetManifest,
  ModuleAssetEntry,
  ModuleType3D,
  MoverType3D,
  MoverToolType3D,
  RailSystem,
  RailType3D,
} from '../types.js';

const beckhoff = (filename: string): ModuleAssetEntry => ({
  glbByRailSystem: { Beckhoff: filename },
});

const dual = (
  beckhoffFile: string,
  hepcoFile: string,
): ModuleAssetEntry => ({
  glbByRailSystem: {
    Beckhoff: beckhoffFile,
    HepcoGfx: hepcoFile,
  },
});

/** Filename mapping per ModuleType3D (relative to assetsBaseUrl). */
export const DEFAULT_MODULE_MANIFEST: Readonly<
  Record<ModuleType3D, ModuleAssetEntry>
> = Object.freeze({
  // Standard AT
  AT2000_0250: beckhoff('AT2000-0250.glb'),
  AT2001_0250: beckhoff('AT2001-0250.glb'),
  AT2002_0250: beckhoff('AT2002-0250.glb'),
  // No standalone AT2002-0249 GLB — only the encoder-adapter variant
  // AT2002_0249_ZX2002_0001 has a delivered STP. Render as placeholder.
  AT2002_0249: { glbByRailSystem: { Beckhoff: null } },
  AT2002_0249_ZX2002_0001: beckhoff('AT2002-0249-ZX2002-0001.glb'),
  AT2000_0233: beckhoff('AT2000-0233.glb'),
  AT2000_0249: beckhoff('AT2000-0249.glb'),
  AT2100_0250: beckhoff('AT2100-0250.glb'),
  AT2102_0250: beckhoff('AT2102-0250.glb'),
  AT2020_0250: beckhoff('AT2020-0250.glb'),
  AT2021_0250: beckhoff('AT2021-0250.glb'),
  AT2025_0250: beckhoff('AT2025-0250.glb'),
  AT2026_0250: beckhoff('AT2026-0250.glb'),
  AT2040_0250: beckhoff('AT2040-0250.glb'),
  AT2041_0250: beckhoff('AT2041-0250.glb'),
  // Prepared but not delivered — loader renders placeholder.
  AT2042_0250: { glbByRailSystem: { Beckhoff: null } },
  AT2140_0250: { glbByRailSystem: { Beckhoff: null } },

  // 0500/0501 are merged to 0500_180 in normalizeXtsConfig; we still expose
  // direct asset entries because the merge can be skipped for half-clothoid
  // visualization in debug paths.
  AT2050_0500: beckhoff('AT2050-0500.glb'),
  AT2050_0501: beckhoff('AT2050-0500.glb'),
  AT2050_0500_180: beckhoff('AT2050-0500.glb'),

  // Eco
  AT2200_0500: beckhoff('AT2200-0500.glb'),
  AT2202_0500: beckhoff('AT2202-0500.glb'),

  // Hygienic ATH — Beckhoff + Hepco GFX rail variants.
  ATH2000_0250: dual('ATH2000-0250.glb', 'ATH2000-0250-0002.glb'),
  ATH2001_0250: dual('ATH2001-0250.glb', 'ATH2001-0250-0002.glb'),
  ATH2002_0250: dual('ATH2002-0250.glb', 'ATH2002-0250-0002.glb'),
  ATH2020_0250: dual('ATH2020-0250.glb', 'ATH2020-0250-0002.glb'),
  ATH2040_0250: dual('ATH2040-0250.glb', 'ATH2040-0250-0002.glb'),
  ATH2041_0250: dual('ATH2041-0250.glb', 'ATH2041-0250-0002.glb'),
  ATH2042_0250: dual('ATH2042-0250.glb', 'ATH2042-0250-0002.glb'),
  ATH2050_0500: dual('ATH2050-0500.glb', 'ATH2050-0500-0002.glb'),
  ATH2050_0501: dual('ATH2050-0500.glb', 'ATH2050-0500-0002.glb'),
  ATH2050_0500_180: dual('ATH2050-0500.glb', 'ATH2050-0500-0002.glb'),
});

export const DEFAULT_MOVER_MANIFEST: Readonly<
  Record<MoverType3D, { glbUrl: string }>
> = Object.freeze({
  AT9011_0050: { glbUrl: 'AT9011-0050.glb' },
  AT9011_0070: { glbUrl: 'AT9011-0070.glb' },
  AT9012_0050: { glbUrl: 'AT9012-0050.glb' },
  AT9014_0055: { glbUrl: 'AT9014-0055-0550.glb' },
  AT9014_0070: { glbUrl: 'AT9014-0070-0550.glb' },
  ATH9011_0075: { glbUrl: 'ATH9011-0075-0550.glb' },
  ATH9013_0075: { glbUrl: 'ATH9013-0075.glb' },
  // Hepco GFX2 1-Track Carrier (S25). Custom remains user-supplied.
  Hepco_GFX2_1TC_S25: { glbUrl: 'GFX2-1TC-S25.glb' },
  Custom: { glbUrl: '' },
});

export const DEFAULT_TOOL_MANIFEST: Readonly<
  Record<MoverToolType3D, { glbUrl: string }>
> = Object.freeze({
  AT8200_1000_0100: { glbUrl: 'AT8200-1000-0100.glb' },
  AT8200_2000_0100: { glbUrl: 'AT8200-2000-0100.glb' },
  Custom: { glbUrl: '' },
});

/**
 * Beckhoff guiding-rail GLBs (the outer rail profile that the mover wheels
 * engage). Rendered per module when `RailSystem === 'Beckhoff'`;
 * Hepco GFX uses a different profile and is handled separately.
 */
export const DEFAULT_RAIL_MANIFEST: Readonly<
  Record<RailType3D, { glbUrl: string }>
> = Object.freeze({
  AT9000_0249: { glbUrl: 'AT9000-0249-rail.glb' },
  AT9000_0250: { glbUrl: 'AT9000-0250-rail.glb' },
  AT9000_0500: { glbUrl: 'AT9000-0500-rail.glb' },
  AT9020_1250: { glbUrl: 'AT9020-1250-rail.glb' },
  AT9025_1466: { glbUrl: 'AT9025-1466-rail.glb' },
  AT9040_0750: { glbUrl: 'AT9040-0750-rail.glb' },
  AT9050_0500: { glbUrl: 'AT9050-0500-rail.glb' },
});

/**
 * ModuleType → guiding-rail mapping.
 *
 * Picks the rail GLB by curve sign + arc length:
 *   - Straights pick the matching-length AT9000 rail (0249 / 0250 / 0500).
 *   - +22.5° curves (AT2020 / AT2021) use AT9020.
 *   - −22.5° curves (AT2025 / AT2026) use AT9025.
 *   - 45° curves  (AT2040 / AT2041 / AT2042 / AT2140) use AT9040.
 *   - The 180° clothoid pair (AT2050 0500/0501 + merged 0500_180) uses
 *     AT9050, whose GLB already covers the full 500 mm path.
 *   - Hygienic ATH modules already include the rail in their module GLB
 *     and therefore map to `null` (no extra rail mesh).
 */
export const MODULE_GUIDING_RAIL_MAP: Readonly<
  Partial<Record<ModuleType3D, RailType3D | null>>
> = Object.freeze({
  // Standard AT — Straights
  AT2000_0250: 'AT9000_0250',
  AT2001_0250: 'AT9000_0250',
  AT2002_0250: 'AT9000_0250',
  AT2002_0249: 'AT9000_0249',
  AT2002_0249_ZX2002_0001: 'AT9000_0249',
  AT2000_0233: 'AT9000_0250',
  AT2000_0249: 'AT9000_0249',
  AT2100_0250: 'AT9000_0250',
  AT2102_0250: 'AT9000_0250',

  // Standard AT — Curves (sign-specific)
  AT2020_0250: 'AT9020_1250', // +22.5°
  AT2021_0250: 'AT9020_1250', // +22.5°
  AT2025_0250: 'AT9025_1466', // −22.5°
  AT2026_0250: 'AT9025_1466', // −22.5°
  AT2040_0250: 'AT9040_0750', // +45°
  AT2041_0250: 'AT9040_0750', // +45°
  AT2042_0250: 'AT9040_0750', // +45°
  AT2140_0250: 'AT9040_0750', // +45°

  // Standard AT — Free-path clothoid halves + merged 180°. AT9050 covers
  // the full 500 mm clothoid in one GLB; the half-types map to it too so
  // a non-normalised config still produces a rail mesh.
  AT2050_0500: 'AT9050_0500',
  AT2050_0501: 'AT9050_0500',
  AT2050_0500_180: 'AT9050_0500',

  // Eco
  AT2200_0500: 'AT9000_0500',
  AT2202_0500: 'AT9000_0500',

  // Hygienic ATH — rail is part of the module GLB.
  ATH2000_0250: null,
  ATH2001_0250: null,
  ATH2002_0250: null,
  ATH2020_0250: null,
  ATH2040_0250: null,
  ATH2041_0250: null,
  ATH2042_0250: null,
  ATH2050_0500: null,
  ATH2050_0501: null,
  ATH2050_0500_180: null,
});

/** Resolve a module's GLB URL with manifest override + rail-system fallback. */
export function resolveModuleGlbUrl(
  moduleType: ModuleType3D,
  rail: RailSystem,
  override?: AssetManifest['modules'],
): string | null {
  const entry = override?.[moduleType] ?? DEFAULT_MODULE_MANIFEST[moduleType];
  if (!entry) return null;
  if (rail === 'HepcoGfx' && entry.glbByRailSystem.HepcoGfx) {
    return entry.glbByRailSystem.HepcoGfx;
  }
  return entry.glbByRailSystem.Beckhoff ?? null;
}

export function resolveMoverGlbUrl(
  moverType: MoverType3D,
  override?: AssetManifest['movers'],
): string {
  const entry = override?.[moverType] ?? DEFAULT_MOVER_MANIFEST[moverType];
  return entry?.glbUrl ?? '';
}

export function resolveToolGlbUrl(
  toolType: MoverToolType3D,
  override?: AssetManifest['tools'],
): string {
  const entry = override?.[toolType] ?? DEFAULT_TOOL_MANIFEST[toolType];
  return entry?.glbUrl ?? '';
}

/**
 * Pick the guiding-rail RailType for a module, or `null` if no separate
 * rail mesh should render (ATH variants, undeclared module types).
 */
export function resolveGuidingRailType(
  moduleType: ModuleType3D,
): RailType3D | null {
  const entry = MODULE_GUIDING_RAIL_MAP[moduleType];
  return entry ?? null;
}

/**
 * Resolve the GLB filename for a guiding-rail type, honoring user-supplied
 * manifest overrides. Returns empty string when the rail is unknown.
 */
export function resolveGuidingRailGlbUrl(
  railType: RailType3D,
  override?: AssetManifest['guidingRails'],
): string {
  const entry = override?.[railType] ?? DEFAULT_RAIL_MANIFEST[railType];
  return entry?.glbUrl ?? '';
}
